import { TextInput as MantineTextInput, TextInputProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedTextInputProps extends TextInputProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const TextInput = forwardRef<HTMLInputElement, ThemedTextInputProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, style, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradientFocus) {
      return (
        <MantineTextInput
          ref={ref}
          style={style}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...accessibilityStyles,
              ...(styles as any)?.label,
            },
            input: {
              paddingLeft: rest.leftSection ? '40px' : undefined,
              paddingRight: rest.rightSection ? '40px' : undefined,
              ...accessibilityStyles,
              ...(styles as any)?.input,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineTextInput
        ref={ref}
        style={style}
        styles={{
          ...styles,
          label: {
            textAlign: 'left !important' as any,
            ...accessibilityStyles,
            ...(styles as any)?.label,
          },
          input: {
            paddingLeft: rest.leftSection ? '40px' : undefined,
            paddingRight: rest.rightSection ? '40px' : undefined,
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            ...accessibilityStyles,
            ...(styles as any)?.input,
          },
        }}
        // Use Mantine's proper styles API for pseudo-selectors
        sx={(theme) => ({
          input: {
            '&:focus': {
              borderColor: `${color} !important`,
              boxShadow: strongFocus
                ? `0 0 0 3px ${color}50 !important`
                : `0 0 0 2px ${color}30 !important`,
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              borderColor: `${color} !important`,
            },
          },
        })}
        {...rest}
      />
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
